import { COLORS } from '../../lib/theme'
import { Icon, type IconName } from '../Icon'
import { Z, useCloseOnEscape } from './topbarChrome'

export interface AccountMenuUser {
  name: string
  email?: string
  /** Initials shown in the avatar disc. */
  avatar?: string
}

export interface AccountMenuItem {
  id: string
  label: string
  icon: IconName
  href?: string
  onSelect?: () => void
}

/**
 * The site's account icon is a bare link to the login page, because nobody is
 * signed in out there. Inside the shell the visitor always is, so the icon
 * carries the profile instead — the "User Profile" half of CLUB-292's scope.
 */
export function AccountMenu({
  open,
  onClose,
  user,
  items,
  onNavigate,
}: {
  open: boolean
  onClose: () => void
  user?: AccountMenuUser
  items: AccountMenuItem[]
  onNavigate?: (href: string) => void
}) {
  useCloseOnEscape(open, onClose)

  if (!open) return null

  return (
    <div
      role="menu"
      aria-label={user ? `${user.name} — account menu` : 'Account menu'}
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        width: 'min(260px, calc(100vw - 32px))',
        zIndex: Z.panel,
        background: COLORS.surfaceContainerLowest,
        color: COLORS.onSurface,
        borderRadius: 12,
        boxShadow: '0 18px 48px rgba(20, 40, 20, 0.22)',
        overflow: 'hidden',
      }}
    >
      {user && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 999,
              background: COLORS.secondaryFixedDim,
              color: COLORS.onSecondaryFixed,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {user.avatar ?? user.name.slice(0, 2).toUpperCase()}
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{user.name}</span>
            {user.email && (
              <span
                style={{
                  display: 'block',
                  fontSize: 12,
                  color: COLORS.onSurfaceVariant,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.email}
              </span>
            )}
          </span>
        </div>
      )}

      <div style={{ padding: 8, display: 'flex', flexDirection: 'column' }}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            onClick={() => {
              onClose()
              item.onSelect?.()
              if (item.href) onNavigate?.(item.href)
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              height: 40,
              padding: '0 10px',
              border: 'none',
              borderRadius: 8,
              background: 'transparent',
              color: COLORS.onSurface,
              fontSize: 14,
              fontWeight: 500,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <Icon name={item.icon} size={18} />
            {item.label}
          </button>
        ))}
      </div>
    </div>
  )
}
